import React, { useEffect, useState } from 'react';
import { supabase } from '../App';

const DEFAULT_CONFIG = {
  id: 1, lhr_peak: 1410, lgw_peak: 901, stn_peak: 677, events_peak: 200000,
  monday_impulsivity: 0.40, tuesday_impulsivity: 0.40, wednesday_impulsivity: 0.50,
  thursday_impulsivity: 0.70, friday_impulsivity: 1.00, saturday_impulsivity: 1.00, sunday_impulsivity: 0.60,
  monday_baseline: 0.40, tuesday_baseline: 0.20, wednesday_baseline: 0.30,
  thursday_baseline: 0.40, friday_baseline: 0.50, saturday_baseline: 0.60, sunday_baseline: 0.50,
  payday_decay_0: 1.00, payday_decay_1: 0.90, payday_decay_2: 0.75, payday_decay_3: 0.60,
  payday_decay_4: 0.45, payday_decay_5: 0.30, payday_decay_6: 0.15, payday_decay_7: 0,
  transport_max_uplift: 0.60, events_max_uplift: 0.40, special_days_max_uplift: 0.40,
  public_holidays_max_uplift: 0.30, payday_max_uplift: 0.08, flights_max_uplift: 0.05,
  strike_intensity_0: 0, strike_intensity_1: 0.25, strike_intensity_2: 0.50, strike_intensity_3: 1.00,
  weather_latitude: 51.5074, weather_longitude: -0.1278,
  weather_active_start_hour: 6, weather_active_end_hour: 24,
  precipitation_max_uplift: 0.15, temperature_max_uplift: 0.05,
  precipitation_intensity_ceiling: 0.65, temperature_intensity_ceiling: 0.75
};

const updateConfigRow = async (patch) => {
  const row = { ...patch, updated_at: new Date().toISOString() };
  const { data, error } = await supabase.from('demand_flight_config').update(row).eq('id', 1).select().single();
  if (error) throw error;
  return data;
};
const WEATHER_FIELDS = [
  ['precipitation_max_uplift', 'Rain max uplift', 'Applied when a day hits the ceiling'],
  ['temperature_max_uplift', 'Temperature max uplift', 'Applied when a day hits the ceiling'],
  ['precipitation_intensity_ceiling', 'Rain ceiling', 'Raw intensity treated as 100%'],
  ['temperature_intensity_ceiling', 'Temperature ceiling', 'Raw intensity treated as 100%']
];
const WEATHER_LOCATION_FIELDS = [
  ['weather_latitude', 'Latitude', '0.0001'],
  ['weather_longitude', 'Longitude', '0.0001'],
  ['weather_active_start_hour', 'Active from (hour)', '1'],
  ['weather_active_end_hour', 'Active to (hour)', '1']
];
const BASELINE_FIELDS = [
  ['monday_baseline', 'Monday', 0.40], ['tuesday_baseline', 'Tuesday', 0.20], ['wednesday_baseline', 'Wednesday', 0.30],
  ['thursday_baseline', 'Thursday', 0.40], ['friday_baseline', 'Friday', 0.50], ['saturday_baseline', 'Saturday', 0.60], ['sunday_baseline', 'Sunday', 0.50]
];
const DEMAND_UPLIFT_FIELDS = [
  ['transport_max_uplift', 'Strikes', 0.60], ['events_max_uplift', 'Events', 0.40], ['special_days_max_uplift', 'Special days', 0.40],
  ['public_holidays_max_uplift', 'Public holidays', 0.30], ['precipitation_max_uplift', 'Rain', 0.15], ['payday_max_uplift', 'Payday', 0.08],
  ['temperature_max_uplift', 'Temperature', 0.05], ['flights_max_uplift', 'Flights', 0.05]
];
const STRIKE_INTENSITY_FIELDS = [
  ['strike_intensity_0', 'Severity 0', 0], ['strike_intensity_1', 'Severity 1', 0.25],
  ['strike_intensity_2', 'Severity 2', 0.50], ['strike_intensity_3', 'Severity 3', 1.00]
];
const WEEKDAYS = [
  ['monday_impulsivity', 'Monday'], ['tuesday_impulsivity', 'Tuesday'], ['wednesday_impulsivity', 'Wednesday'],
  ['thursday_impulsivity', 'Thursday'], ['friday_impulsivity', 'Friday'], ['saturday_impulsivity', 'Saturday'], ['sunday_impulsivity', 'Sunday']
];
const PAYDAY_DECAY = [
  ['payday_decay_0', '0 days'], ['payday_decay_1', '1 day'], ['payday_decay_2', '2 days'], ['payday_decay_3', '3 days'],
  ['payday_decay_4', '4 days'], ['payday_decay_5', '5 days']
];
const percentage = (value) => `${(Number(value || 0) * 100).toFixed(0)}%`;
const maskKey = (value) => {
  const key = String(value || '');
  if (!key) return 'Not set';
  return key.length <= 4 ? '••••' : `${'•'.repeat(Math.min(key.length - 4, 24))}${key.slice(-4)}`;
};

function ApiKeyForm({ title, column, placeholder, value, onSaved }) {
  const [draft, setDraft] = useState('');
  const [reveal, setReveal] = useState(false);
  const [status, setStatus] = useState('');

  const validatePredictHqKey = async () => {
    const response = await fetch('/api/predicthq/validate');
    const body = await response.json();
    if (!response.ok || !body.ok) throw new Error(body.error || `PredictHQ returned ${response.status}.`);
    return body.status;
  };

  const save = async (event) => {
    event.preventDefault();
    const nextKey = draft.trim();
    if (!nextKey) return setStatus('Enter an API key before saving.');
    try {
      const data = await updateConfigRow({ [column]: nextKey });
      onSaved(data);
      setDraft('');
      setReveal(false);
      if (column !== 'predicthq_api_key') return setStatus('API key updated');
      setStatus('API key saved. Checking PredictHQ...');
      const responseStatus = await validatePredictHqKey();
      return setStatus(`API key updated and verified (PredictHQ HTTP ${responseStatus}).`);
    } catch (error) {
      return setStatus(`Save failed: ${error.message}`);
    }
  };

  return <form className="config-section" onSubmit={save}>
    <h3>{title}</h3>
    <div className="config-form">
      <div className="config-total"><span>Current key</span><strong className="mono">{reveal ? (value || 'Not set') : maskKey(value)}</strong></div>
      <label>New key<input type="password" autoComplete="off" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={placeholder} /></label>
      <div className="config-key-actions">
        <button type="button" className="button muted" onClick={() => setReveal((current) => !current)} disabled={!value}>{reveal ? 'Hide' : 'Show'}</button>
        <button className="button primary" type="submit" disabled={!supabase}>Update key</button>
      </div>
      {status && <span className="dashboard-toast">{status}</span>}
    </div>
  </form>;
}

function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return <div className="config-section">
    <button type="button" className="config-section-header" onClick={() => setOpen((current) => !current)} aria-expanded={open}>
      <span>{title}</span>
      <span className="config-section-toggle">{open ? '−' : '+'}</span>
    </button>
    {open && <div className="config-section-body">{children}</div>}
  </div>;
}

export default function DemandConfig() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [status, setStatus] = useState('Loading configuration...');
  const [modelView, setModelView] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setStatus('Supabase is not configured');
      return;
    }
    supabase.from('demand_flight_config').select('*').eq('id', 1).maybeSingle().then(({ data, error }) => {
      if (error) setStatus(error.code === '42P01' ? 'Config table missing: run supabase-schema.sql in Supabase' : `Load failed: ${error.message}`);
      else {
        if (data) setConfig(data);
        setStatus('Ready');
      }
    });
  }, []);

  const setValue = (name, value) => setConfig((current) => ({ ...current, [name]: value }));
  const totalPeak = Number(config.lhr_peak || 0) + Number(config.lgw_peak || 0) + Number(config.stn_peak || 0);
  const sortedUpliftFields = [...DEMAND_UPLIFT_FIELDS].sort((first, second) => Number(config[second[0]] ?? second[2]) - Number(config[first[0]] ?? first[2]));

  const save = async (event) => {
    event.preventDefault();
    const intensityFields = [...WEEKDAYS.map(([name]) => name), ...PAYDAY_DECAY.map(([name]) => name)];
    const peakFields = ['lhr_peak', 'lgw_peak', 'stn_peak', 'events_peak'];
    if (peakFields.some((name) => !Number.isFinite(Number(config[name])) || Number(config[name]) < 0)
      || intensityFields.some((name) => !Number.isFinite(Number(config[name])) || Number(config[name]) < 0 || Number(config[name]) > 1)) {
      setStatus('Enter non-negative peaks and intensity values from 0.00 to 1.00.');
      return;
    }
    const row = { ...DEFAULT_CONFIG, id: 1, updated_at: new Date().toISOString() };
    [...peakFields, ...intensityFields, ...BASELINE_FIELDS.map(([name]) => name), ...DEMAND_UPLIFT_FIELDS.map(([name]) => name), ...STRIKE_INTENSITY_FIELDS.map(([name]) => name)].forEach((name) => { row[name] = Number(config[name]); });
    [...WEATHER_FIELDS, ...WEATHER_LOCATION_FIELDS].forEach(([name]) => { row[name] = Number(config[name]); });
    try {
      const data = await updateConfigRow(row);
      setConfig(data);
      setStatus('Saved');
    } catch (error) {
      setStatus(`Save failed: ${error.message}`);
    }
  };

  return <section className="config-panel">
    <div className="live-header"><div><span className="eyebrow">MODEL CONFIGURATION</span><h2>Demand inputs</h2></div><span className="dashboard-toast">{status}</span></div>
    <form onSubmit={save}>
      <CollapsibleSection title="Historical peaks">
        <div className="config-form">
          <label>Heathrow (LHR)<input type="number" min="0" step="1" value={config.lhr_peak} onChange={(event) => setValue('lhr_peak', event.target.value)} /></label>
          <label>Gatwick (LGW)<input type="number" min="0" step="1" value={config.lgw_peak} onChange={(event) => setValue('lgw_peak', event.target.value)} /></label>
          <label>Stansted (STN)<input type="number" min="0" step="1" value={config.stn_peak} onChange={(event) => setValue('stn_peak', event.target.value)} /></label>
          <div className="config-total"><span>Total flight peak</span><strong>{totalPeak.toLocaleString('en-GB')}</strong></div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="Events attendance">
        <div className="config-form">
          <label>Attendance peak<input type="number" min="0" step="1" value={config.events_peak} onChange={(event) => setValue('events_peak', event.target.value)} /><small>Highest daily total in live events</small></label>
        </div>
      </CollapsibleSection>
      <CollapsibleSection title="Day impulsivity">
        <div className="config-value-grid">{WEEKDAYS.map(([name, label]) => <label key={name}>{label}<input type="number" min="0" max="1" step="0.01" value={config[name]} onChange={(event) => setValue(name, event.target.value)} /><small>{percentage(config[name])}</small></label>)}</div>
      </CollapsibleSection>
      <CollapsibleSection title="Weekday baseline demand">
        <div className="config-value-grid">{BASELINE_FIELDS.map(([name, label, fallback]) => <label key={name}>{label}<input type="number" min="0" max="1" step="0.01" value={config[name] ?? fallback} onChange={(event) => setValue(name, event.target.value)} /><small>{percentage(config[name] ?? fallback)}</small></label>)}</div>
      </CollapsibleSection>
      <CollapsibleSection title="Demand feature maximum uplifts">
        <div className="config-value-grid">{DEMAND_UPLIFT_FIELDS.map(([name, label, fallback]) => <label key={name}>{label}<input type="number" min="0" max="1" step="0.01" value={config[name] ?? fallback} onChange={(event) => setValue(name, event.target.value)} /><small>{percentage(config[name] ?? fallback)}</small></label>)}</div>
      </CollapsibleSection>
      <CollapsibleSection title="Strike severity intensity">
        <div className="config-value-grid">{STRIKE_INTENSITY_FIELDS.map(([name, label, fallback]) => <label key={name}>{label}<input type="number" min="0" max="1" step="0.01" value={config[name] ?? fallback} onChange={(event) => setValue(name, event.target.value)} /><small>{percentage(config[name] ?? fallback)}</small></label>)}</div>
      </CollapsibleSection>
      <CollapsibleSection title="Payday decay since last working day">
        <div className="config-value-grid">{PAYDAY_DECAY.map(([name, label]) => <label key={name}>{label}<input type="number" min="0" max="1" step="0.01" value={config[name]} onChange={(event) => setValue(name, event.target.value)} /><small>{percentage(config[name])}</small></label>)}</div>
      </CollapsibleSection>
      <CollapsibleSection title="Weather uplift">
        <div className="config-value-grid">{WEATHER_FIELDS.map(([name, label, hint]) => <label key={name}>{label}<input type="number" min="0" max="1" step="0.01" value={config[name]} onChange={(event) => setValue(name, event.target.value)} /><small>{hint}</small></label>)}</div>
      </CollapsibleSection>
      <CollapsibleSection title="Weather location and window">
        <div className="config-value-grid">{WEATHER_LOCATION_FIELDS.map(([name, label, step]) => <label key={name}>{label}<input type="number" step={step} value={config[name]} onChange={(event) => setValue(name, event.target.value)} /></label>)}</div>
      </CollapsibleSection>
      <button className="button primary config-save" type="submit" disabled={!supabase}>Save configuration</button>
    </form>
    <button className="button muted model-view-toggle" type="button" onClick={() => setModelView((current) => !current)}>{modelView ? 'Hide model view' : 'View model'}</button>
    {modelView && <section className="model-view" aria-label="Demand feature model settings"><div className="model-view-header"><div><span className="eyebrow">MODEL CONTROLS</span><h2>Feature maximum uplifts</h2></div><span>Largest first</span></div><div className="model-feature-list">{sortedUpliftFields.map(([name, label, fallback], index) => { const value = Number(config[name] ?? fallback); const enabled = value > 0; return <div className={`model-feature model-feature-${index}`} key={name}><strong>{label}</strong><span>{percentage(value)}</span><button type="button" className={`model-switch${enabled ? ' is-on' : ''}`} aria-pressed={enabled} onClick={() => setValue(name, enabled ? 0 : fallback)}><span className="model-switch-light" />{enabled ? 'ON' : 'OFF'}</button></div>; })}</div></section>}
    <ApiKeyForm title="PredictHQ API key" column="predicthq_api_key" placeholder="Paste new PredictHQ key" value={config.predicthq_api_key} onSaved={setConfig} />
  </section>;
}
